import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import io, { Socket } from "socket.io-client";
import api from "../utils/api";
import "../styles/ConsultationRoom.css";
import MicrophoneSettingsModal from "../components/MicrophoneSettingsModal";
import { supportedSpeechLanguages } from "../constants/languages";

// 대화 턴 관리를 위한 인터페이스
interface ConsultationTurn {
  currentSpeaker: "professor" | "student";
  isActive: boolean;
  startTime: number;
}

// 번역 결과 인터페이스
interface TranslationResult {
  classId: string;
  speaker: string;
  originalText: string;
  originalLanguage: string;
  translations: { [language: string]: string }; // 키-값 객체 형태로 변경
  timestamp: number | Date;
  isFinal: boolean;
  translationStatus?: "pending" | "completed";
}

// 마이크 장치 인터페이스
interface MicrophoneDevice {
  deviceId: string;
  label: string;
}

// 언어 설정 모달 타입 (교수/학생)
type LanguageSettingType = "professor" | "student" | null;

const ConsultationRoom: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [classData, setClassData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [translations, setTranslations] = useState<TranslationResult[]>([]);
  const [turn, setTurn] = useState<ConsultationTurn>({
    currentSpeaker: "professor", // 초기값은 교수부터 시작
    isActive: false,
    startTime: 0,
  });
  const [currentTranscription, setCurrentTranscription] = useState<{
    id: string;
    text: string;
    isFinal: boolean;
    translations: any;
    lastUpdated: number;
    isProcessing: boolean;
  } | null>(null);

  // 마이크 설정 관련 상태
  const [showMicrophoneModal, setShowMicrophoneModal] = useState(false);
  const [microphoneDevices, setMicrophoneDevices] = useState<
    MicrophoneDevice[]
  >([]);
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState<string>("");

  // 언어 설정 관련 상태
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [professorLanguage, setProfessorLanguage] = useState<string>("ko-KR");
  const [studentLanguage, setStudentLanguage] = useState<string>("en-US");
  const [currentLanguageSetting, setCurrentLanguageSetting] =
    useState<LanguageSettingType>(null);

  // 글꼴 크기 관련 상태
  const [fontSize, setFontSize] = useState<number>(16);

  // 글꼴 크기 옵션 배열
  const fontSizeOptions = [
    10, 12, 14, 16, 18, 20, 22, 24, 28, 32, 36, 40, 44, 48,
  ];

  // 글꼴 크기 변경 함수
  const changeFontSize = (size: number) => {
    setFontSize(size);
    localStorage.setItem("consultationFontSize", size.toString());
  };

  // 오디오 처리를 위한 Refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const translationsContainerRef = useRef<HTMLDivElement | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  // 오디오 로그 타임스탬프 추적용 ref 추가
  const lastAudioLogTimeRef = useRef<number | null>(null);
  const lastAudioSentLogTimeRef = useRef<number | null>(null);

  // 턴 상태 추적을 위한 Ref 추가
  const turnIsActiveRef = useRef<boolean>(false);
  const currentSpeakerRef = useRef<"professor" | "student">("professor");

  // 상담학생 프로필 모달 관련 상태
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const [studentProfile, setStudentProfile] = useState({
    name: "",
    nation: "",
    email: "",
  });
  const [formErrors, setFormErrors] = useState({
    name: false,
    nation: false,
  });

  const handleTranslationResult = useCallback((data: TranslationResult) => {
    // 번역 결과가 없거나 유효하지 않은 경우 무시
    if (!data || !data.originalText) {
      console.warn("유효하지 않은 번역 결과 형식:", data);
      return;
    }

    // 다시 한번 translations 필드 정규화 - 안전장치
    let normalizedData = { ...data };
    if (typeof data.translations === "string") {
      normalizedData.translations = {
        [data.originalLanguage === "ko" ? "en-US" : "ko-KR"]: data.translations,
      };
    }

    // 번역 결과를 누적하여 저장 (이전 결과 유지)
    setTranslations((prevTranslations) => {
      // 새로운 결과와 이전 결과 병합
      const newResults = [...prevTranslations];

      // 같은 원본 텍스트가 있는지 확인 (매칭 로직 단순화)
      const existingIndex = newResults.findIndex(
        (t) => t.originalText === data.originalText
      );

      if (existingIndex >= 0) {
        // 이미 있는 결과는 업데이트
        newResults[existingIndex] = normalizedData;
      } else {
        // 새 결과는 추가
        newResults.push(normalizedData);
      }

      return newResults;
    });
  }, []);

  // 상담실 정보 가져오기
  useEffect(() => {
    const fetchClassData = async () => {
      try {
        setIsLoading(true);
        const response = await api.get(`/classes/${classId}`);
        setClassData(response.data.result);

        // 레이아웃이 CONSULTATION이 아니면 경고
        if (response.data.result.layout !== "CONSULTATION") {
          setError("이 클래스는 1대1 상담 형식이 아닙니다.");
        }

        // 저장된 언어 설정 불러오기
        const savedProfessorLanguage =
          localStorage.getItem("professorLanguage");
        const savedStudentLanguage = localStorage.getItem("studentLanguage");

        if (savedProfessorLanguage) {
          setProfessorLanguage(savedProfessorLanguage);
        }

        if (savedStudentLanguage) {
          setStudentLanguage(savedStudentLanguage);
        }

        // 저장된 글꼴 크기 설정 로드
        const savedFontSize = localStorage.getItem("consultationFontSize");
        if (savedFontSize) {
          const parsedSize = parseInt(savedFontSize, 10);
          if (!isNaN(parsedSize) && fontSizeOptions.includes(parsedSize)) {
            setFontSize(parsedSize);
          }
        }

        // 진행 중인 상담인 경우 기존 번역 기록 복원
        if (response.data.result.status === "IN_PROGRESS") {
          await fetchExistingTranslations();
        }
      } catch (err) {
        console.error("상담실 정보 불러오기 오류:", err);
        setError("상담실 정보를 불러올 수 없습니다.");
      } finally {
        setIsLoading(false);
      }
    };

    // 기존 번역 기록을 가져오는 함수
    const fetchExistingTranslations = async () => {
      try {
        // Redis에서 현재 진행 중인 번역 기록 가져오기
        const translationsResponse = await api.get(
          `/classes/${classId}/translations/current`
        );

        if (
          translationsResponse.data.result &&
          Array.isArray(translationsResponse.data.result)
        ) {
          const existingTranslations = translationsResponse.data.result;

          // 번역 결과를 적절한 형식으로 변환
          const formattedTranslations = existingTranslations.map(
            (item: any, index: number) => {
              // translations 필드 처리
              let translationsData = {};

              if (item.translations) {
                if (
                  typeof item.translations === "object" &&
                  item.translations !== null
                ) {
                  // 객체 형태인 경우 그대로 사용
                  translationsData = item.translations;
                } else if (typeof item.translations === "string") {
                  // 문자열인 경우 - 기본 언어로 처리

                  // 원본 언어에 따라 타겟 언어 결정
                  const targetLang =
                    item.originalLanguage === "ko" ? "en-US" : "ko-KR";
                  translationsData = {
                    [targetLang]: item.translations as string,
                  };
                }
              }

              return {
                classId: item.classId || classId,
                speaker: item.speaker || item.role || "professor",
                originalText: item.originalText || item.sourceText,
                originalLanguage:
                  item.originalLanguage || item.sourceLanguage || "ko",
                translations: translationsData,
                timestamp: new Date(item.timestamp).getTime(),
                isFinal: true,
              };
            }
          );

          // 기존 번역 기록을 상태에 설정
          if (formattedTranslations.length > 0) {
            formattedTranslations.forEach((translation: TranslationResult) => {
              handleTranslationResult(translation);
            });
          }
        }
      } catch (error) {
        console.warn("상담실 기존 번역 기록 가져오기 실패:", error);
      }
    };

    if (classId) {
      fetchClassData();
    }
  }, [classId]);

  // 마이크 장치 목록 가져오기
  useEffect(() => {
    const loadMicrophoneDevices = async () => {
      try {
        // 미디어 장치 권한 요청
        await navigator.mediaDevices.getUserMedia({ audio: true });

        // 사용 가능한 장치 목록 가져오기
        const devices = await navigator.mediaDevices.enumerateDevices();

        // 오디오 입력 장치만 필터링
        const audioInputs = devices
          .filter((device) => device.kind === "audioinput")
          .map((device) => ({
            deviceId: device.deviceId,
            label: device.label || `마이크 ${device.deviceId.slice(0, 5)}...`,
          }));

        setMicrophoneDevices(audioInputs);

        // 로컬 스토리지에서 선택된 마이크 불러오기
        const savedMicrophoneId = localStorage.getItem("selectedMicrophoneId");

        // 저장된 마이크가 있고 현재 장치 목록에 존재하면 설정
        if (
          savedMicrophoneId &&
          audioInputs.some((device) => device.deviceId === savedMicrophoneId)
        ) {
          setSelectedMicrophoneId(savedMicrophoneId);
        }
        // 아니면 첫 번째 장치 선택
        else if (audioInputs.length > 0) {
          setSelectedMicrophoneId(audioInputs[0].deviceId);
        }
      } catch (err) {
        console.error("마이크 장치 목록 가져오기 오류:", err);
        setError("마이크 장치 목록을 가져올 수 없습니다.");
      }
    };

    loadMicrophoneDevices();
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handleConsultationTrans = (data: any) => {
      if (data.originalText) {
        // 서버에서 받은 translations를 올바른 형식으로 변환
        let normalizedData = { ...data };

        // translations가 문자열인 경우 객체로 변환
        if (typeof data.translations === "string") {
          normalizedData.translations = {
            [data.originalLanguage === "ko" ? "en-US" : "ko-KR"]:
              data.translations,
          };
        }

        // ** 중요: 번역 결과가 최종 결과인 경우에만 저장 조건 완화 **
        // isFinal만 확인하고 translationStatus는 확인하지 않음
        if (data.isFinal) {
          // 번역 결과 처리 함수 호출하여 저장
          handleTranslationResult(normalizedData);
        }

        // 최종 결과가 아닐 때 (음성 인식 중)
        if (!data.isFinal) {
          // 기존 currentTranscription이 없거나 새로운 내용이 이전 내용을 포함하는 경우만 업데이트
          const newText = data.originalText.trim().toLowerCase();
          const currentText =
            currentTranscription?.text?.trim().toLowerCase() || "";

          // 새 텍스트가 기존 텍스트를 포함하거나, 기존 텍스트가 없는 경우에만 업데이트
          if (
            !currentText ||
            newText.includes(currentText) ||
            currentText.includes(newText)
          ) {
            // 현재 진행 중인 텍스트 업데이트 (노란색 박스)
            setCurrentTranscription({
              id: `speech-${Date.now()}`,
              text: data.originalText,
              isFinal: false,
              translations: normalizedData.translations || {},
              lastUpdated: Date.now(),
              isProcessing: true, // 번역 처리 중
            });
          }
        }

        // 최종 결과인 경우 (음성 인식 완료, 번역 완료)
        if (data.isFinal) {
          // 현재 진행 중인 텍스트와 관련된 경우 currentTranscription 초기화
          if (currentTranscription) {
            const currentText = currentTranscription.text.trim().toLowerCase();
            const finalText = data.originalText.trim().toLowerCase();

            // 내용이 유사한 경우 초기화
            if (
              currentText === finalText ||
              currentText.includes(finalText) ||
              finalText.includes(currentText)
            ) {
              setCurrentTranscription(null);
            }
          }
        }
      }
    };

    socket.on("speechTranslation", handleConsultationTrans);
    socket.on("disconnect", (reason) => {
      setIsConnected(false);
    });

    return () => {
      socket.off("speechTranslation", handleConsultationTrans);
      socket.off("disconnect");
    };
  }, [socket, classId]);

  // socket.io 연결 설정을 위한 useEffect 개선
  useEffect(() => {
    const socket = io(`${process.env.REACT_APP_SOCKET_URL}/speech`);
    setSocket(socket);
    setIsConnected(true);

    socket.on("connect", () => {
      // 상담실 데이터가 로드된 후에만 소켓 이벤트 전송
      if (classData) {
        if (classData.status === "IN_PROGRESS") {
          // 진행 중인 상담인 경우 재연결
          socket.emit("reconnectClass", {
            classId: classId,
            className: classData.title,
            professorId: classData.professorId,
            professorLanguage: "ko",
            targetLanguages: [],
          });
        } else {
          // 새로운 상담 시작
          socket.emit("createClass", {
            classId: classId,
            professorId: user?.username,
          });
        }
      }
    });

    // 클린업 함수: 컴포넌트 언마운트 시 소켓 연결 정리
    return () => {
      if (socket) {
        socket.emit("stopDeepgramSTT");
        socket.emit("stopConsultation", classId);
        socket.disconnect();
      }
    };
  }, [classId, user?.username, classData]); // classData를 의존성에 추가

  // 자동 스크롤
  useEffect(() => {
    if (translationsContainerRef.current) {
      scrollToBottom(translationsContainerRef.current);
    }
  }, [translations]);

  // 스크롤을 맨 아래로 이동하는 함수
  const scrollToBottom = (element: HTMLDivElement | null) => {
    if (element) {
      element.scrollTop = element.scrollHeight;
    }
  };

  // 턴 상태 변경 감지를 위한 useEffect 추가
  useEffect(() => {
    // Ref 값 업데이트
    turnIsActiveRef.current = turn.isActive;
    currentSpeakerRef.current = turn.currentSpeaker;

    // 턴 상태가 변경될 때마다 실행되는 코드
    if (turn.isActive) {
      if (workletNodeRef.current) {
        try {
          workletNodeRef.current.port.postMessage({
            type: "turnState",
            isActive: turn.isActive,
          });
        } catch (error) {
          console.error("AudioWorklet에 메시지 전송 중 오류:", error);
        }
      }
    }
  }, [turn]);

  // 오디오 처리 설정 - AudioWorkletNode 사용으로 업데이트
  const setupAudioProcessing = async (
    stream: MediaStream
  ): Promise<boolean> => {
    try {
      // 오디오 컨텍스트 생성
      const context = new (window.AudioContext ||
        (window as any).webkitAudioContext)({
        sampleRate: 16000,
      });

      // 오디오 컨텍스트 참조 저장
      audioContextRef.current = context;

      // 오디오 소스 노드 생성
      const source = context.createMediaStreamSource(stream);

      // AudioWorklet 모듈 로드
      await context.audioWorklet.addModule("/audioProcessor.js");

      // AudioWorkletNode 생성
      const workletNode = new AudioWorkletNode(context, "audio-processor");

      // AudioWorkletNode 참조 저장
      workletNodeRef.current = workletNode;

      // 메시지 이벤트 핸들러 설정
      workletNode.port.onmessage = (event) => {
        if (!turnIsActiveRef.current && event.data.type === "audioData") {
          return; // 턴 비활성화 상태면 즉시 리턴
        }

        if (event.data.type === "init" || event.data.type === "debug") {
          // 디버그 메시지 로깅
          return;
        }

        if (event.data.type === "audioData" && event.data.buffer) {
          // 턴이 활성화된 상태일 때만 전송
          if (turnIsActiveRef.current) {
            try {
              // AudioWorklet에서 받은 buffer는 이미 ArrayBuffer 형태
              const buffer = event.data.buffer;

              // Int16Array로 래핑하여 Uint8Array로 변환
              const int16Array = new Int16Array(buffer);
              const uint8Array = new Uint8Array(buffer);

              // 너무 많은 로그가 발생하므로 4초에 한 번씩만 로그 출력
              const now = Date.now();
              if (
                !lastAudioLogTimeRef.current ||
                now - lastAudioLogTimeRef.current > 4000
              ) {
                lastAudioLogTimeRef.current = now;
              }

              // 오디오 데이터를 소켓을 통해 서버로 전송
              if (buffer.byteLength > 0 && socket && isConnected) {
                try {
                  // 상담용 오디오 데이터 전송 (consultationAudioSend 이벤트 사용)
                  sendAudioData(buffer);

                  // 로그 출력 (너무 많은 로그를 방지하기 위해 약 10초에 한 번씩만 출력)
                  if (
                    !lastAudioSentLogTimeRef.current ||
                    now - lastAudioSentLogTimeRef.current > 10000
                  ) {
                    lastAudioSentLogTimeRef.current = now;
                  }
                } catch (socketError) {
                  console.error("오디오 데이터 소켓 전송 오류:", socketError);
                }
              }
            } catch (error) {
              console.error("오디오 데이터 처리 중 오류:", error);
            }
          }
        }
      };

      // 오디오 그래프 연결
      source.connect(workletNode);
      workletNode.connect(context.destination);

      return true;
    } catch (err) {
      console.error("오디오 처리 설정 오류:", err);
      // 스택 트레이스 출력
      if (err instanceof Error) {
        console.error("스택 트레이스:", err.stack);
      }
      setError(
        "오디오 처리 설정에 실패했습니다: " +
          (err instanceof Error ? err.message : String(err))
      );
      return false;
    }
  };

  // 오디오 데이터 서버로 전송
  const sendAudioData = (audioBuffer: ArrayBuffer) => {
    // useRef를 사용하여 최신 상태 가져오기
    const isActive = turnIsActiveRef.current;
    const currentSpeaker = currentSpeakerRef.current;

    if (!socket || !isConnected) {
      return;
    }

    // Ref 상태 확인
    if (!isActive) {
      return;
    }

    // 현재 말하는 사람의 역할 (Ref에서 가져옴)
    const speakerRole = currentSpeaker;

    // 오디오 데이터를 서버에 전송 (원본 ArrayBuffer 사용)
    const data = {
      classId,
      speaker: speakerRole,
      audioData: audioBuffer, // 원본 ArrayBuffer 직접 전송
      speakingLanguage:
        speakerRole === "professor" ? professorLanguage : studentLanguage,
      targetLanguage:
        speakerRole === "professor" ? studentLanguage : professorLanguage,
      sampleRate: 16000, // 샘플레이트 16000으로 유지
      isActive: true, // 활성화 상태 명시적 표시
    };

    socket.emit("consultationAudioSend", data);
  };

  // 음성 인식 시작
  const startRecording = async () => {
    try {
      // 마이크 액세스 권한 요청 (선택된 마이크 사용)
      const constraints = {
        audio: {
          deviceId: selectedMicrophoneId
            ? { exact: selectedMicrophoneId }
            : undefined,
        },
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      audioStreamRef.current = stream;

      // 오디오 처리 설정
      const setupSuccess = await setupAudioProcessing(stream);
      if (!setupSuccess) {
      }
    } catch (err) {
      console.error("마이크 액세스 오류:", err);
      setError("마이크 접근에 실패했습니다. 권한을 확인해주세요.");
    }
  };

  // 음성 인식 중지
  const stopRecording = () => {
    // 턴 상태 Ref 값 즉시 업데이트 (이전 코드는 여기서 업데이트하지 않음)
    turnIsActiveRef.current = false;

    // 오디오 처리 중지
    if (workletNodeRef.current) {
      try {
        // 턴 상태 업데이트 전송
        workletNodeRef.current.port.postMessage({
          type: "turnState",
          isActive: false,
        });
        // 연결 해제
        workletNodeRef.current.disconnect();
        workletNodeRef.current = null;
      } catch (e) {
        console.error("workletNode 연결 해제 중 오류:", e);
      }
    }

    // 오디오 컨텍스트 종료
    if (audioContextRef.current) {
      try {
        audioContextRef.current
          .close()
          .catch((err) => console.error("AudioContext 닫기 오류:", err));
        audioContextRef.current = null;
      } catch (e) {
        console.error("AudioContext 종료 중 오류:", e);
      }
    }

    // 마이크 스트림 중지
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => {
        track.stop();
      });
      audioStreamRef.current = null;
    }

    // 서버에 STT 중지 이벤트 전송
    if (socket && isConnected && classId) {
      socket.emit("stopConsultation", classId);
    }
  };

  // 말하기 버튼 핸들러
  const handleSpeakButtonClick = (role: "professor" | "student") => {
    // 현재 말하는 사람이 이미 active하고 같은 역할이면 턴 종료
    if (turn.currentSpeaker === role && turn.isActive) {
      changeSpeaker(role, false);
    }
    // 다른 경우 턴 시작 (이전 턴이 활성화되어 있다면 먼저 종료)
    else {
      // 이전에 활성화된 턴이 있다면 먼저 종료
      if (turn.isActive) {
        stopRecording();

        // 서버에 턴 변경 이벤트 전송 (현재 턴을 종료)
        if (socket) {
          socket.emit("startConsultation", {
            classId,
            speaker: turn.currentSpeaker,
            speakingLanguage:
              turn.currentSpeaker === "professor"
                ? professorLanguage
                : studentLanguage,
            targetLanguage:
              turn.currentSpeaker === "professor"
                ? studentLanguage
                : professorLanguage,
            sampleRate: 16000, // 샘플레이트 16000으로 변경
          });
        }
      }

      // 잠시 대기 후 새 턴 시작
      setTimeout(() => {
        changeSpeaker(role, true);
      }, 300);
    }
  };

  // 턴 변경 함수
  const changeSpeaker = (speaker: "professor" | "student", active: boolean) => {
    // 먼저 Ref 값 업데이트 (즉시 반영)
    turnIsActiveRef.current = active;
    currentSpeakerRef.current = speaker;

    // 워크렛 노드가 있으면 턴 상태 전달
    if (workletNodeRef.current) {
      workletNodeRef.current.port.postMessage({
        type: "turnState",
        isActive: active,
      });
    }

    // 턴 상태 React 상태 업데이트 (UI 반영용)
    setTurn({
      currentSpeaker: speaker,
      isActive: active,
      startTime: Date.now(),
    });

    // 현재 클라이언트가 단일 사용자이므로 녹음 상태 제어
    if (active) {
      startRecording();
    } else {
      stopRecording();
    }

    // 추가: 상태 변경 후 서버에 이벤트 전송 (startConsultation)
    if (socket && active) {
      socket.emit("startConsultation", {
        classId,
        speaker: speaker,
        speakingLanguage:
          speaker === "professor" ? professorLanguage : studentLanguage,
        targetLanguage:
          speaker === "professor" ? studentLanguage : professorLanguage,
        sampleRate: 16000, // 샘플레이트 16000으로 유지
      });
    }
  };

  // 상담 종료 핸들러
  const handleConsultationEnd = async () => {
    if (window.confirm("정말로 상담을 종료하시겠습니까?")) {
      try {
        // 녹음 중지
        stopRecording();

        await api.post(`/classes/${classId}/end`);

        // 대시보드로 이동
        navigate("/dashboard");
      } catch (err) {
        console.error("상담 종료 오류:", err);
        setError("상담 종료에 실패했습니다.");
      }
    }
  };

  // 마이크 설정 모달 열기
  const handleOpenMicrophoneModal = () => {
    setShowMicrophoneModal(true);
  };

  // 마이크 설정 저장
  const handleSaveMicrophoneSettings = (microphoneId: string) => {
    // 선택된 마이크 업데이트
    setSelectedMicrophoneId(microphoneId);

    // 선택된 마이크 로컬 스토리지에 저장
    localStorage.setItem("selectedMicrophoneId", microphoneId);

    // 현재 녹음 중이면 중지하고 다시 시작
    if (turn.isActive) {
      stopRecording();
      startRecording();
    }

    setShowMicrophoneModal(false);
  };

  // 모달 닫기 핸들러
  const handleCloseMicrophoneModal = () => {
    setShowMicrophoneModal(false);
  };

  // 언어 설정 모달 열기 (교수 언어)
  const handleOpenProfessorLanguageModal = () => {
    setCurrentLanguageSetting("professor");
    setShowLanguageModal(true);
  };

  // 언어 설정 모달 열기 (학생 언어)
  const handleOpenStudentLanguageModal = () => {
    setCurrentLanguageSetting("student");
    setShowLanguageModal(true);
  };

  // 언어 설정 모달 닫기
  const handleCloseLanguageModal = () => {
    setShowLanguageModal(false);
    setCurrentLanguageSetting(null);
  };

  // 언어 설정 저장
  const handleSelectLanguage = (languageCode: string) => {
    if (currentLanguageSetting === "professor") {
      setProfessorLanguage(languageCode);
      localStorage.setItem("professorLanguage", languageCode);
    } else if (currentLanguageSetting === "student") {
      setStudentLanguage(languageCode);
      localStorage.setItem("studentLanguage", languageCode);
    }

    // 서버에 언어 설정 전송
    if (socket && isConnected) {
      socket.emit("setLanguages", {
        classId,
        userId: user?.id,
        professorLanguage:
          currentLanguageSetting === "professor"
            ? languageCode
            : professorLanguage,
        studentLanguage:
          currentLanguageSetting === "student" ? languageCode : studentLanguage,
      });
    }

    setShowLanguageModal(false);
    setCurrentLanguageSetting(null);
  };

  // 언어 코드에서 언어 이름 가져오기
  const getLanguageName = (languageCode: string): string => {
    const language = supportedSpeechLanguages.find(
      (lang) => lang.code === languageCode
    );
    return language ? language.koreanName : languageCode;
  };

  // 상담학생 프로필 입력 핸들러
  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setStudentProfile((prev) => ({
      ...prev,
      [name]: value,
    }));

    // 필수 입력 필드인 경우 오류 상태 업데이트
    if (name === "name" || name === "nation") {
      setFormErrors((prev) => ({
        ...prev,
        [name]: value.trim() === "",
      }));
    }
  };

  // 상담학생 프로필 제출 핸들러
  const handleProfileSubmit = async () => {
    // 필수 입력값 검증
    const nameError = studentProfile.name.trim() === "";
    const nationError = studentProfile.nation.trim() === "";

    setFormErrors({
      name: nameError,
      nation: nationError,
    });

    if (nameError || nationError) {
      return; // 필수 항목이 비어있으면 제출하지 않음
    }

    try {
      // email이 빈 문자열인 경우 해당 필드 제외
      const profileData = studentProfile.email.trim()
        ? { ...studentProfile }
        : { name: studentProfile.name, nation: studentProfile.nation };

      const response = await api.post(
        `/classes/${classId}/counsel`,
        profileData
      );

      // 성공 후 모달 닫기 및 폼 초기화
      setShowProfileModal(false);
      setStudentProfile({ name: "", nation: "", email: "" });

      // 성공 메시지
      alert("상담학생 프로필이 성공적으로 저장되었습니다.");
    } catch (err: any) {
      console.error("상담학생 프로필 저장 오류:", err);
      alert("상담학생 프로필 저장 중 오류가 발생했습니다.");
    }
  };

  // 모달 열림/닫힘 시 body 스크롤 제어
  useEffect(() => {
    const hasOpenModal =
      showMicrophoneModal || showLanguageModal || showProfileModal;

    if (hasOpenModal) {
      // 모달이 열릴 때 현재 스크롤 위치 저장
      const scrollY = window.scrollY;
      document.body.style.top = `-${scrollY}px`;
      document.body.classList.add("modal-open");
    } else {
      // 모달이 닫힐 때 스크롤 위치 복원
      const scrollY = parseInt(document.body.style.top || "0", 10);
      document.body.classList.remove("modal-open");
      document.body.style.top = "";
      window.scrollTo(0, -scrollY);
    }

    // 컴포넌트 언마운트 시 정리
    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.top = "";
    };
  }, [showMicrophoneModal, showLanguageModal, showProfileModal]);

  if (isLoading) {
    return <div className="loading">상담실 정보를 불러오는 중...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  // 현재 사용자 역할 확인
  const isProf = user?.role === "PROFESSOR";
  const myRole = isProf ? "professor" : "student";

  // 내 턴인지 확인
  const isMyTurn = turn.currentSpeaker === myRole;

  // 현재 선택된 마이크 이름 찾기
  const selectedMicrophone = microphoneDevices.find(
    (device) => device.deviceId === selectedMicrophoneId
  );

  return (
    <div className="consultation-room">
      {/* 상단 헤더 */}
      <header className="consultation-header">
        <div className="class-info">
          <h1>{classData?.title || "1:1 상담"}</h1>
          <p className="class-id">Class ID: {classId}</p>
        </div>
        <div className="header-actions">
          {isProf && (
            <button
              className="end-consultation-btn"
              onClick={handleConsultationEnd}
            >
              상담 종료
            </button>
          )}
        </div>
      </header>

      {/* 메인 콘텐츠 */}
      <div className="consultation-content">
        {/* 대화 제어 패널 */}
        <div className="speakers-panel">
          <div
            className={`speaker-card ${
              turn.currentSpeaker === "professor" && turn.isActive
                ? "active"
                : ""
            }`}
          >
            <div className="speaker-info">
              <div className="speaker-avatar">👨‍🏫</div>
              <div className="speaker-name">교수</div>
              <div className="speaker-language">
                사용 언어: {getLanguageName(professorLanguage)}
              </div>
            </div>
            <button
              className={`speak-button ${
                turn.currentSpeaker === "professor" && turn.isActive
                  ? "stop-speaking"
                  : "start-speaking"
              }`}
              onClick={() => handleSpeakButtonClick("professor")}
              disabled={turn.currentSpeaker === "student" && turn.isActive}
            >
              {turn.currentSpeaker === "professor" && turn.isActive
                ? "말하기 종료"
                : "말하기 시작"}
            </button>
          </div>

          <div className="turn-arrow">
            {turn.isActive
              ? turn.currentSpeaker === "professor"
                ? "👉"
                : "👈"
              : "⟷"}
          </div>

          <div
            className={`speaker-card ${
              turn.currentSpeaker === "student" && turn.isActive ? "active" : ""
            }`}
          >
            <div className="speaker-info">
              <div className="speaker-avatar">👨‍🎓</div>
              <div className="speaker-name">학생</div>
              <div className="speaker-language">
                사용 언어: {getLanguageName(studentLanguage)}
              </div>
            </div>
            <button
              className={`speak-button ${
                turn.currentSpeaker === "student" && turn.isActive
                  ? "stop-speaking"
                  : "start-speaking"
              }`}
              onClick={() => handleSpeakButtonClick("student")}
              disabled={turn.currentSpeaker === "professor" && turn.isActive}
            >
              {turn.currentSpeaker === "student" && turn.isActive
                ? "말하기 종료"
                : "말하기 시작"}
            </button>
          </div>
        </div>

        {/* 번역 결과 패널 */}
        <div className="translations-panel">
          <div className="translations-header">
            <h3>번역 결과</h3>
            <div className="font-size-controls">
              <label className="font-size-label">글꼴 크기:</label>
              <select
                value={fontSize}
                onChange={(e) => changeFontSize(parseInt(e.target.value))}
                className="font-size-select"
                title="글꼴 크기 선택"
              >
                {fontSizeOptions.map((size) => (
                  <option key={size} value={size}>
                    {size}px
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div
            className="translations-content"
            ref={translationsContainerRef}
            style={{
              height: "100%",
              overflowY: "auto",
              padding: "10px 15px",
            }}
          >
            {translations.length === 0 && !currentTranscription ? (
              <div className="empty-translations">
                <p>아직 대화 내용이 없습니다.</p>
                <p>버튼을 눌러 말하기를 시작해보세요.</p>
              </div>
            ) : (
              <>
                {translations.map((item, index) => {
                  return (
                    <div
                      key={index}
                      className={`translation-item ${
                        item.speaker === "professor"
                          ? "professor-message"
                          : "student-message"
                      }`}
                    >
                      <div className="message-header">
                        <span className="speaker-label">
                          {item.speaker === "professor" ? "교수" : "학생"}
                        </span>
                      </div>
                      <div
                        className="message-content"
                        style={{ fontSize: `${fontSize}px` }}
                      >
                        {item.originalText}
                      </div>

                      {item.translations &&
                        typeof item.translations === "object" && (
                          <div className="translation-content">
                            {Object.entries(item.translations).map(
                              ([language, text], i) => (
                                <div
                                  key={i}
                                  className="translation"
                                  style={{ fontSize: `${fontSize}px` }}
                                >
                                  <span className="language-label"></span>{" "}
                                  {text}
                                </div>
                              )
                            )}
                          </div>
                        )}
                      {/* 문자열인 경우 직접 표시 */}
                      {item.translations &&
                        typeof item.translations === "string" && (
                          <div className="translation-content">
                            <div
                              className="translation"
                              style={{ fontSize: `${fontSize}px` }}
                            >
                              <span className="language-label">번역:</span>{" "}
                              {item.translations}
                            </div>
                          </div>
                        )}
                    </div>
                  );
                })}

                {/* 현재 진행 중인 번역 표시 */}
                {currentTranscription && (
                  <div
                    className={`translation-item current-transcription ${
                      turn.currentSpeaker === "professor"
                        ? "professor-message"
                        : "student-message"
                    }`}
                  >
                    <div className="message-header">
                      <span className="speaker-label">
                        {turn.currentSpeaker === "professor" ? "교수" : "학생"}
                      </span>
                      <span className="transcription-status">
                        {currentTranscription.isProcessing
                          ? "번역 중..."
                          : "음성 인식 중..."}
                      </span>
                    </div>
                    <div
                      className="message-content"
                      style={{ fontSize: `${fontSize}px` }}
                    >
                      {currentTranscription.text}
                    </div>

                    {/* 현재 진행 중인 번역 결과 표시 */}
                    {currentTranscription.translations &&
                      typeof currentTranscription.translations === "object" &&
                      Object.keys(currentTranscription.translations).length >
                        0 && (
                        <div className="translation-content">
                          {Object.entries(
                            currentTranscription.translations as Record<
                              string,
                              string
                            >
                          ).map(([language, text], i) => (
                            <div
                              key={i}
                              className="translation"
                              style={{ fontSize: `${fontSize}px` }}
                            >
                              {text}
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* 하단 설정 영역 */}
      <footer className="settings-footer">
        <div className="settings-container">
          <button
            className="btn-settings microphone-settings-btn"
            onClick={handleOpenMicrophoneModal}
            title="마이크 설정"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="settings-icon"
            >
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </svg>
            마이크 설정
          </button>

          {/* 상담학생 프로필 버튼 */}
          <button
            className="btn-settings student-profile-btn"
            onClick={() => setShowProfileModal(true)}
            title="상담학생 프로필 작성"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="settings-icon"
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            상담학생 프로필
          </button>

          <button
            className="btn-settings language-settings-btn professor-language-btn"
            onClick={handleOpenProfessorLanguageModal}
            title="교수가 사용하는 언어 설정"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="settings-icon"
            >
              <path d="M2 5h12M9 3v2M3 11h18"></path>
            </svg>
            교수 사용 언어: {getLanguageName(professorLanguage)}
          </button>

          <button
            className="btn-settings language-settings-btn student-language-btn"
            onClick={handleOpenStudentLanguageModal}
            title="학생이 사용하는 언어 설정"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="settings-icon"
            >
              <path d="M3 17h12M13 15v4"></path>
              <circle cx="18" cy="17" r="3"></circle>
            </svg>
            학생 사용 언어: {getLanguageName(studentLanguage)}
          </button>
        </div>
      </footer>

      {/* MicrophoneSettingsModal 컴포넌트 사용 */}
      <MicrophoneSettingsModal
        isOpen={showMicrophoneModal}
        onClose={handleCloseMicrophoneModal}
        selectedMicrophoneId={selectedMicrophoneId}
        onSave={handleSaveMicrophoneSettings}
      />

      {/* 언어 설정 모달 */}
      {showLanguageModal && (
        <div className="modal-overlay" onClick={handleCloseLanguageModal}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {currentLanguageSetting === "professor"
                  ? "교수 사용 언어 설정"
                  : "학생 사용 언어 설정"}
              </h3>
              <button
                className="modal-close-btn"
                onClick={handleCloseLanguageModal}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="settings-content">
                <p>
                  {currentLanguageSetting === "professor"
                    ? "교수가 말할 때 사용하는 언어를 선택해주세요"
                    : "학생이 말할 때 사용하는 언어를 선택해주세요"}
                </p>
                <div className="language-explanation">
                  <small>
                    {currentLanguageSetting === "professor"
                      ? `교수가 ${getLanguageName(
                          professorLanguage
                        )}로 말하면, 학생에게는 ${getLanguageName(
                          studentLanguage
                        )}로 번역되어 보입니다.`
                      : `학생이 ${getLanguageName(
                          studentLanguage
                        )}로 말하면, 교수에게는 ${getLanguageName(
                          professorLanguage
                        )}로 번역되어 보입니다.`}
                  </small>
                </div>
                <div className="language-radio-group">
                  {supportedSpeechLanguages.map((lang) => (
                    <label key={lang.code} className="language-radio-item">
                      <input
                        type="radio"
                        name="language"
                        value={lang.code}
                        checked={
                          currentLanguageSetting === "professor"
                            ? professorLanguage === lang.code
                            : studentLanguage === lang.code
                        }
                        onChange={() => handleSelectLanguage(lang.code)}
                      />
                      <span>
                        {lang.koreanName} ({lang.nativeName})
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={handleCloseLanguageModal}>
                취소
              </button>
              <button
                className="btn-primary"
                onClick={handleCloseLanguageModal}
              >
                확인
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 상담학생 프로필 모달 */}
      {showProfileModal && (
        <div
          className="modal-overlay"
          onClick={() => setShowProfileModal(false)}
        >
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>상담학생 프로필 작성</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowProfileModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="settings-content">
                <div className="form-group">
                  <label htmlFor="name">
                    이름 <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={studentProfile.name}
                    onChange={handleProfileChange}
                    className={formErrors.name ? "error" : ""}
                  />
                  {formErrors.name && (
                    <span className="error-message">
                      이름은 필수입력 항목입니다
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="nation">
                    국가 <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    id="nation"
                    name="nation"
                    value={studentProfile.nation}
                    onChange={handleProfileChange}
                    className={formErrors.nation ? "error" : ""}
                  />
                  {formErrors.nation && (
                    <span className="error-message">
                      국가는 필수입력 항목입니다
                    </span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="email">이메일</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={studentProfile.email}
                    onChange={handleProfileChange}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn-cancel"
                onClick={() => setShowProfileModal(false)}
              >
                취소
              </button>
              <button className="btn-primary" onClick={handleProfileSubmit}>
                저장
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultationRoom;
